/**
 * Het formulier voor een standaardtekst: nieuw, of een bestaande bewerken.
 *
 * Links de tekst zoals de beheerder hem invoert (of laat schrijven), rechts
 * de standaardtekst op 3F die wordt opgeslagen. Omzetten naar 3F kan pas
 * vanaf 800 woorden. De alinea's in het rechtervak worden gescheiden door
 * een witregel.
 *
 * Nieuw en bewerken verschillen op een paar punten: bij bewerken kun je het
 * teksttype nog kiezen, staat de opgeslagen tekst al links én rechts (zodat
 * je meteen kunt opslaan), en is er een knop om links naar rechts te
 * kopiëren.
 */
import React from 'react';
import { ArrowRight, Check, RefreshCw, Sparkles } from 'lucide-react';
import { TEKSTTYPEN } from '../../../keuzelijsten';

export const MINIMUM_WOORDEN = 800;
export const LEEG = { titel: '', beschrijving: '', inputText: '', standardText: '', text_type: 'informatief' };

export function telWoorden(tekst) {
  if (!tekst) return 0;
  return tekst.trim().split(/\s+/).filter(woord => woord.length > 0).length;
}

/** De tekst in het rechtervak als lijst alinea's. */
export const alineasVan = (tekst) => tekst.split('\n\n').filter(p => p.trim());

export function TekstFormulier({ bewerken, tekst, wijzig, omzetten, schrijven, onOmzetten, onSchrijven, onOpslaan, onAnnuleer }) {
  const soort = TEKSTTYPEN.find(t => t.value === tekst.text_type);
  const links = telWoorden(tekst.inputText);
  const genoeg = links >= MINIMUM_WOORDEN;
  const zet = (veld) => (e) => wijzig({ ...tekst, [veld]: e.target.value });

  const verstuur = (e) => {
    e.preventDefault();
    onOpslaan();
  };

  return (
    <form onSubmit={verstuur} className="card" style={{ background: bewerken ? 'var(--blauw-vlak)' : 'var(--vlak)' }}>
      {bewerken ? (
        <h3 className="mb-16">Tekst bewerken - {soort?.label || 'Tekst'}</h3>
      ) : (
        <h3 style={{ marginBottom: '16px', color: 'var(--inkt)' }}>{soort?.label || 'Nieuwe tekst'}</h3>
      )}

      <div className={bewerken ? undefined : 'grid-2'} style={bewerken ? { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' } : undefined}>
        <div className="form-group">
          <label>Titel</label>
          <input type="text" className="input" value={tekst.titel} onChange={zet('titel')} required />
        </div>
        <div className="form-group">
          <label>{soort?.beschrijving || 'Beschrijving'}</label>
          <input type="text" className="input" value={tekst.beschrijving} onChange={zet('beschrijving')}
            placeholder={soort?.beschrijving || 'Korte beschrijving'} />
        </div>
        {bewerken && (
          <div className="form-group">
            <label>Teksttype</label>
            <select className="input" value={tekst.text_type} onChange={zet('text_type')}>
              {TEKSTTYPEN.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Een tekst laten schrijven over de titel en de beschrijving */}
      <div style={{ textAlign: 'center', margin: '16px 0', padding: '16px', background: bewerken ? 'var(--blauw-vlak-3)' : 'var(--blauw-vlak-2)', borderRadius: '8px' }}>
        <p className="text-muted mb-8">
          <Sparkles size={16} style={{ verticalAlign: 'middle', marginRight: 4 }} />
          {bewerken
            ? 'Genereer een nieuwe tekst op basis van de titel en beschrijving.'
            : 'Geen tekst? Laat AI een tekst genereren op basis van de titel en beschrijving.'}
        </p>
        <button type="button" className="btn btn-primary" onClick={onSchrijven}
          disabled={schrijven || !tekst.titel.trim()} style={{ padding: '12px 32px' }}>
          {schrijven ? <><RefreshCw size={16} className="spinning" /> Tekst aan het genereren...</> : <><Sparkles size={16} /> Genereer een tekst</>}
        </button>
        {schrijven && (
          <p className="text-muted text-small mt-8">We zoeken op internet en schrijven een tekst. Dit kan tot 3 minuten duren...</p>
        )}
      </div>

      <div className="grid-2" style={{ gap: '24px', alignItems: 'start' }}>
        {/* links: de ingevoerde tekst */}
        <div className="form-group">
          <div className="flex-between">
            <label>{bewerken ? 'Brontekst (bewerk hier)' : 'Ingevoerde tekst'}</label>
            <span style={{ color: genoeg ? 'var(--goed-2)' : 'var(--stil)', fontWeight: genoeg ? 'bold' : 'normal' }}>
              {links} woorden
            </span>
          </div>
          <textarea
            className="input"
            rows={12}
            value={tekst.inputText}
            onChange={zet('inputText')}
            placeholder={bewerken
              ? "Bewerk de tekst hier. Klik op 'Opnieuw omzetten' voor een nieuwe conversie."
              : 'Voer hier de originele tekst in. De tekst moet minimaal 800 woorden bevatten om omgezet te kunnen worden naar de standaardtekst (3F niveau).'}
            spellCheck={false}
            required={!bewerken}
          />
          {bewerken && (
            <p className="text-muted text-small mt-8">
              Tip: Bewerk deze tekst en klik op "Opnieuw omzetten" voor een nieuwe 3F versie, of kopieer wijzigingen direct naar rechts.
            </p>
          )}
        </div>

        {/* rechts: de standaardtekst, die wordt opgeslagen */}
        <div className="form-group">
          <div className="flex-between">
            <label>{bewerken ? 'Standaardtekst (3F) - wordt opgeslagen' : 'Standaardtekst (3F niveau)'}</label>
            <span style={{ color: 'var(--stil)' }}>{telWoorden(tekst.standardText)} woorden</span>
          </div>
          <textarea
            className="input"
            rows={12}
            value={tekst.standardText}
            onChange={zet('standardText')}
            placeholder={bewerken
              ? 'Dit is de tekst die wordt opgeslagen'
              : 'Hier verschijnt de omgezette standaardtekst na conversie. Paragrafen worden gescheiden door een lege regel.'}
            required={bewerken}
            style={{ background: bewerken ? 'var(--goed-vlak-2)' : (tekst.standardText ? 'var(--wit)' : 'var(--vlak-2)') }}
            spellCheck={false}
          />
          {bewerken && (
            <>
              <p className="text-muted text-small mt-8">Dit veld wordt opgeslagen. Je kunt hier direct aanpassingen maken.</p>
              <p className="text-small mt-8" style={{ color: 'var(--waarschuw-tekst)' }} data-testid="waarschuwing-niveaus">
                Let op: verander je de standaardtekst zelf, dan vervallen de teksten op alle niveaus. Hij moet dan opnieuw
                op elk niveau gemaakt en goedgekeurd worden (tabblad Niveauteksten); tot dan kan hij niet in een les, en wie
                nog niet begon, ziet een les met deze tekst niet. Alleen de titel of de beschrijving veranderen kan zonder.
              </p>
            </>
          )}
        </div>
      </div>

      {bewerken ? (
        tekst.inputText && (
          <div style={{ textAlign: 'center', margin: '16px 0' }}>
            <div className="flex gap-8" style={{ justifyContent: 'center' }}>
              <button type="button" className="btn btn-secondary" onClick={() => wijzig({ ...tekst, standardText: tekst.inputText })}
                style={{ padding: '12px 24px' }}>
                <ArrowRight size={16} /> Kopieer naar rechts
              </button>
              <button type="button" className={`btn ${genoeg ? 'btn-success' : 'btn-primary'}`} onClick={onOmzetten}
                disabled={omzetten || !genoeg} style={{ padding: '12px 24px' }}>
                {omzetten ? <><RefreshCw size={16} className="spinning" /> Bezig met omzetten...</> : <><RefreshCw size={16} /> Opnieuw omzetten naar 3F</>}
              </button>
            </div>
            {omzetten && <p className="text-muted text-small mt-8">Dit kan tot 2 minuten duren voor lange teksten...</p>}
          </div>
        )
      ) : (
        <div style={{ textAlign: 'center', margin: '16px 0' }}>
          <button type="button" className={`btn ${genoeg ? 'btn-success' : 'btn-primary'}`} onClick={onOmzetten}
            disabled={omzetten || !genoeg} style={{ padding: '12px 32px' }}>
            {omzetten
              ? <><RefreshCw size={16} className="spinning" /> Bezig met omzetten...</>
              : <><ArrowRight size={16} /> Omzetten naar {soort?.label?.toLowerCase() || 'tekst'} (3F)</>}
          </button>
          {omzetten && <p className="text-muted text-small mt-8">Dit kan tot 2 minuten duren voor lange teksten...</p>}
          {!omzetten && !genoeg && (
            <p className="text-muted text-small mt-8">Nog {MINIMUM_WOORDEN - links} woorden nodig voor omzetting</p>
          )}
        </div>
      )}

      <div className="flex gap-8">
        <button type="submit" className="btn btn-success" disabled={!tekst.standardText || !tekst.titel}>
          <Check size={16} /> Opslaan en sluiten
        </button>
        <button type="button" className="btn btn-secondary" onClick={onAnnuleer}>Annuleren</button>
      </div>
    </form>
  );
}
